import { ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

import { DateTime } from "luxon";
import { CustomDate, getDateDimensionNames } from "modules/customer/insights/cost/customDate";

export class MonthlyRevenue {
    public readonly storeId: number;
    public readonly date: DateTime;
    public readonly customDate: CustomDate;
    public readonly revenue: number;

    constructor(
        storeId: number,
        date: DateTime,
        customDate: CustomDate,
        revenue: number
    ) {
        this.storeId = storeId;
        this.date = date;
        this.customDate = customDate;
        this.revenue = revenue;
    }
}

export const loadMonthlyRevenue = (
    costReferenceDate: DateTime,
    useCustomCalendar: boolean,
    storeId?: number,
    similarStoresIds?: number[]
): AppThunk<Promise<MonthlyRevenue[]>> => async (dispatch) => {
    try {
        if (!costReferenceDate || !storeId || !similarStoresIds) {
            return [];
        }

        const priorFiveYearsToReferenceDate = costReferenceDate.minus({ years: 5 }).plus({ days: 1 }).startOf("day");
        const dateDimensions = getDateDimensionNames(useCustomCalendar);

        const storeAndSimilarStoreIds = [storeId].concat(...similarStoresIds).map(String);

        const salesQuery = {
            measures: ["F_Sales.SumLineValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [priorFiveYearsToReferenceDate, costReferenceDate.endOf('day')]
            }],
            filters: [{
                member: "D_Store.PK_Store",
                operator: "equals",
                values: storeAndSimilarStoreIds
            }],
            dimensions: [
                "D_Store.PK_Store",
                `D_Date.${dateDimensions.monthPeriodStartDate}`,
                `D_Date.${dateDimensions.monthPeriodNumber}`,
                "D_Date.QuarterNo",
                `D_Date.${dateDimensions.yearNumber}`,
            ],
            order: [[`D_Date.${dateDimensions.monthPeriodStartDate}`, "asc"]]
        };

        const salesPromise = dispatch(cubeLoad(salesQuery));

        const result = await Promise.all([salesPromise]) as unknown as ResultSet[];

        const salesRawData = result[0].rawData();

        return salesRawData.map(salesRow => {
            return new MonthlyRevenue(
                Number(salesRow["D_Store.PK_Store"]),
                DateTime.fromISO(salesRow[`D_Date.${dateDimensions.monthPeriodStartDate}`], { zone: "utc" }),
                new CustomDate(
                    DateTime.fromISO(salesRow[`D_Date.${dateDimensions.monthPeriodStartDate}`], { zone: "utc" }),
                    Number(salesRow[`D_Date.${dateDimensions.monthPeriodNumber}`]),
                    Number(salesRow["D_Date.QuarterNo"]),
                    Number(salesRow[`D_Date.${dateDimensions.yearNumber}`]),
                ),
                Number(salesRow["F_Sales.SumLineValue"])
            );
        });
    } catch (error) {
        dispatch(logError("Error loading MonthlyRevenue.", error));
        throw error;
    }
};
